import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Button,
    Flex,
    Input,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { SCREEN_MD } from 'utils/constants';
import { isValidAddress } from 'utils/general';
import { AddressBookEntry, CreateAddressBookEntry } from 'types/addressBook';
import { createAddressBookEntry, updateAddressBookEntry } from 'api/addressBookAPI';

interface AddressBookModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    entry?: AddressBookEntry | null;
    prefillAddress?: string;
    userAccountId?: string;
}

const AddressBookModal = ({ isOpen, onClose, onSave, entry, prefillAddress, userAccountId }: AddressBookModalProps) => {
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [notes, setNotes] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);


    const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

    const isEditing = !!entry;
    const isAddressInvalid = address !== '' && !isValidAddress(address);
    const isUserOwnAddress = !!userAccountId && address.trim() === userAccountId;

    useEffect(() => {
        if (entry) {
            setName(entry.name);
            setAddress(entry.address);
            setNotes(entry.notes || '');
        } else {
            setName('');
            setAddress(prefillAddress || '');
            setNotes('');
        }
        setServerError(null);
    }, [entry, prefillAddress, isOpen]);

    const isFormValid = name.trim() !== '' && address.trim() !== '' && !isAddressInvalid && !isUserOwnAddress;

    const handleSubmit = async () => {
        if (!isFormValid) return;
        setIsLoading(true);
        try {
            const data: CreateAddressBookEntry = {
                name: name.trim(),
                address: address.trim(),
                notes: notes.trim() === '' ? '' : notes.trim(),
            };
            if (isEditing && entry) {
                await updateAddressBookEntry(entry._id, data);
            } else {
                await createAddressBookEntry(data);
            }
            onSave();
            onClose();
        } catch (e: any) {
            console.error('Failed to save address book entry:', e);
            const msg = e.response?.data?.message;
            if (msg) {
                setServerError(Array.isArray(msg) ? msg[0] : msg);
            } else {
                setServerError('Failed to save contact. Please try again.');
            }
        } finally {

            setIsLoading(false);
        }
    };

    return (
        <Modal onClose={onClose} isOpen={isOpen} isCentered>
            <ModalOverlay />
            <ModalContent
                maxWidth="486px"
                w="full"
                mx={isScreenMedium ? 4 : 0}
            >
                <ModalHeader pt={6} px={6} pb={0}>
                    <Flex alignItems="center" flexDirection="column">
                        <Text mt={4} size="2xl" variant="bodyBold">
                            {isEditing ? 'Edit Contact' : 'Add Contact'}
                        </Text>
                    </Flex>
                    <ModalCloseButton
                        mt={2}
                        mr={2}
                        color="#565656"
                        _hover={{ border: 'none' }}
                        _focusVisible={{ outline: 'none' }}
                    />
                </ModalHeader>
                <ModalBody pb={10} pt={4} px={8}>
                    <Flex direction="column" gap={4}>
                        <Flex direction="column" gap={1}>
                            <Text mb={1} fontWeight={500}>Name</Text>
                            <Input
                                h={12}
                                borderRadius={16}
                                placeholder="e.g. Alice, Exchange, DAO Treasury"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Flex>

                        <Flex direction="column" gap={1}>
                            <Text mb={1} fontWeight={500}>Address</Text>
                            <Input
                                h={12}
                                borderRadius={16}
                                placeholder="0.0.xxxxx"
                                value={address}
                                isInvalid={isAddressInvalid || isUserOwnAddress || !!serverError}
                                onChange={(e) => {
                                    setAddress(e.target.value);
                                    setServerError(null);
                                }}
                            />
                            {isAddressInvalid && (
                                <Text color="red.500" size="xs" mt={1}>
                                    Invalid Hedera account ID
                                </Text>
                            )}
                            {isUserOwnAddress && (
                                <Text color="red.500" size="xs" mt={1}>
                                    You cannot add your own address to the address book
                                </Text>
                            )}
                            {serverError && (
                                <Text color="red.500" size="xs" mt={1}>
                                    {serverError}
                                </Text>
                            )}

                        </Flex>

                        <Flex direction="column" gap={1}>
                            <Text mb={1} fontWeight={500}>Notes (optional)</Text>
                            <Textarea
                                borderRadius={16}
                                placeholder="Any additional notes about this contact"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                            />
                        </Flex>

                        <Button
                            w="full"
                            mt={2}
                            variant="primary"
                            borderRadius={6}
                            onClick={handleSubmit}
                            isLoading={isLoading}
                            isDisabled={!isFormValid}
                        >
                            {isEditing ? 'Save Changes' : 'Add Contact'}
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddressBookModal;
